import React from "react";
import { Program } from "../../services/api";
import {
  XMarkIcon,
  CurrencyDollarIcon,
  EyeIcon,
  EyeSlashIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";

interface ProgramDetailModalProps {
  program: Program | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (id: string) => void;
  onToggleVisibility: (id: string) => void;
}

const ProgramDetailModal: React.FC<ProgramDetailModalProps> = ({
  program,
  isOpen,
  onClose,
  onEdit,
  onToggleVisibility,
}) => {
  if (!isOpen || !program) return null;

  const isIncomplete =
    !program.descriptionCustomer ||
    !program.conditions ||
    !program.cashbackRate ||
    !program.countries?.length;
  const isAwinOnly = program.affiliateUrl && isIncomplete;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                  {program.logo ? (
                    <img
                      src={program.logo}
                      alt={program.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  ) : (
                    <span className="text-gray-500 dark:text-gray-400 font-semibold text-2xl">
                      {program.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {program.name}
                  </h2>
                  {isAwinOnly && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 mt-2">
                      Needs Setup
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Status and Actions */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center space-x-4">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      program.visibility
                        ? "bg-success-100 dark:bg-success-900 text-success-800 dark:text-success-200"
                        : "bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-300"
                    }`}
                  >
                    {program.visibility ? "Active" : "Hidden"}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center space-x-2">
                    {window.innerWidth < 768 ? (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {program._id.slice(0, 6)}...{program._id.slice(-6)}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {program._id}
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => onToggleVisibility(program._id)}
                    className="flex items-center px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-gray-700 dark:text-gray-300"
                  >
                    {program.visibility ? (
                      <EyeSlashIcon className="h-4 w-4 mr-1" />
                    ) : (
                      <EyeIcon className="h-4 w-4 mr-1" />
                    )}
                    {program.visibility ? "Hide" : "Show"}
                  </button>
                  <button
                    onClick={() => onEdit(program._id)}
                    className={`flex items-center px-3 py-1 text-sm rounded transition-colors ${
                      isAwinOnly
                        ? "bg-orange-600 text-white hover:bg-orange-700"
                        : "bg-primary-600 text-white hover:bg-primary-700"
                    }`}
                  >
                    {isAwinOnly ? "Setup" : "Edit"}
                  </button>
                </div>
              </div>

              {/* Affiliate URL - Full Width */}
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 block">
                  Affiliate URL
                </label>
                <p className="text-sm text-gray-900 dark:text-white break-all font-mono">
                  {program.affiliateUrl}
                </p>
              </div>

              {/* Key Metrics - Horizontal Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Cashback Rate Card */}
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
                    Cashback (
                    {program.cashbackType === "fixed" ? "Fixed" : "Percent"})
                  </label>
                  <div className="flex items-center">
                    <CurrencyDollarIcon className="h-5 w-5 mr-2 text-success-600" />
                    {program.cashbackType === "fixed" ? (
                      program.cashbackFixedAmount ? (
                        <span className="text-lg font-bold text-success-600 dark:text-success-400">
                          {program.cashbackFixedAmount} EP
                        </span>
                      ) : (
                        <span className="text-sm text-orange-600 dark:text-orange-400 italic">
                          Not set
                        </span>
                      )
                    ) : program.cashbackRate ? (
                      <span className="text-lg font-bold text-success-600 dark:text-success-400">
                        {program.cashbackRate}%
                      </span>
                    ) : (
                      <span className="text-sm text-orange-600 dark:text-orange-400 italic">
                        Not set
                      </span>
                    )}
                  </div>
                </div>

                {/* Category Card */}
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
                    Categories/Subcategories
                  </label>
                  {/* Display multiple categories if available */}
                  {program.categories && program.categories.length > 0 ? (
                    <div className="flex flex-wrap gap-1 mb-1">
                      {program.categories.map((cat, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400"
                        >
                          {cat}
                        </span>
                      ))}
                    </div>
                  ) : program.category ? (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400">
                      {typeof program.category === "object" &&
                      program.category !== null
                        ? (program.category as any).name ||
                          (program.category as any).categoryName ||
                          "Category"
                        : program.category}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                      No category
                    </span>
                  )}
                  {/* Display multiple subcategories if available */}
                  {program.subcategories && program.subcategories.length > 0 ? (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {program.subcategories.map((sub, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-400"
                        >
                          {sub}
                        </span>
                      ))}
                    </div>
                  ) : program.subcategory ? (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-400 mt-1">
                      {typeof program.subcategory === "object" &&
                      program.subcategory !== null
                        ? (program.subcategory as any).name || "Subcategory"
                        : program.subcategory}
                    </span>
                  ) : null}
                </div>

                {/* Countries Card */}
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
                    Countries
                  </label>
                  {program.countries?.length ? (
                    <div className="flex flex-wrap gap-1">
                      {program.countries.map((country, index) => (
                        <span
                          key={index}
                          className="px-2 py-0.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded text-xs"
                        >
                          {country}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-orange-600 dark:text-orange-400 italic">
                      Not set
                    </span>
                  )}
                </div>
              </div>

              {/* Timestamps */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    Created
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {formatDate(program.createdAt)}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    Last Updated
                  </label>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {formatDate(program.updatedAt)}
                  </p>
                </div>
              </div>

              {/* Description and Conditions */}
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 block">
                    Customer Description
                  </label>
                  {program.descriptionCustomer ? (
                    <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                      {program.descriptionCustomer}
                    </p>
                  ) : (
                    <p className="text-sm text-orange-600 dark:text-orange-400 italic">
                      Customer description not set
                    </p>
                  )}
                </div>

                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 block">
                    Conditions
                  </label>
                  {program.conditions ? (
                    <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                      {program.conditions}
                    </p>
                  ) : (
                    <p className="text-sm text-orange-600 dark:text-orange-400 italic">
                      Conditions not set
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramDetailModal;
